import { type CandidateStatus } from "./types/candidate";

type StoredStatus = Exclude<CandidateStatus, "in_review">;

const ACTIVE: readonly StoredStatus[] = [
  "pool",
  "longlist",
  "selected",
  "outreach",
  "prequal",
  "interview",
  "recommendation",
];

/** A copy of `vidario-backend/candidates/services/candidate_status.py`: a
 * candidate moves forward one pipeline step at a time, the Recommendation is
 * reachable from any active stage, and every stage after the Longlist but
 * Platziert can go back to it (#162). */
export const CANDIDATE_TRANSITIONS: Record<StoredStatus, readonly StoredStatus[]> = {
  pool: ["longlist", "recommendation", "rejected"],
  longlist: ["selected", "pool", "recommendation", "rejected"],
  selected: ["outreach", "longlist", "recommendation", "rejected"],
  outreach: ["prequal", "longlist", "recommendation", "rejected"],
  prequal: ["interview", "longlist", "recommendation", "rejected"],
  interview: ["recommendation", "longlist", "rejected"],
  recommendation: ["placed", "longlist", "rejected"],
  placed: ["rejected"],
  rejected: ACTIVE,
};

/** The forward moves the candidate sidebar offers, in pipeline order. */
const SIDEBAR_MOVES = ["selected", "outreach", "prequal", "interview", "recommendation"] as const;
export type SidebarMove = (typeof SIDEBAR_MOVES)[number];

/** Vidario's own steps and the Recommendation are the admin's work. */
const ADMIN_ONLY: ReadonlySet<SidebarMove> = new Set(["prequal", "interview", "recommendation"]);

/** Which forward moves the sidebar shows for this candidate and user (#22):
 * only those the backend accepts. Rejecting and un-rejecting have their own
 * controls, so they aren't part of this list. */
export function candidateMoves(
  status: CandidateStatus,
  { isAdmin }: { isAdmin: boolean },
): SidebarMove[] {
  if (status === "in_review" || status === "rejected") return [];
  const allowed = CANDIDATE_TRANSITIONS[status];
  return SIDEBAR_MOVES.filter(
    (move) => allowed.includes(move) && (isAdmin || !ADMIN_ONLY.has(move)),
  );
}

/** Where a candidate can come back to the Longlist from, named as on the
 * status badges (`CANDIDATE_STATUS_MAP` in @vidario/ui) (#162). */
const MOVE_BACK_STAGE_LABEL: Partial<Record<StoredStatus, string>> = {
  selected: "Ausgewählt",
  outreach: "Outreach",
  prequal: "Vorqualifizierung",
  interview: "Tiefeninterview",
  recommendation: "Empfehlung",
};

/** Whether this user can move the candidate back to the Longlist, and whether
 * it asks first (#162): a recruiter takes back only their own pick, which needs
 * no dialog; the later stages are the team's, where the candidate may already
 * have been contacted or recommended. */
export function moveBack(
  status: CandidateStatus,
  { isAdmin }: { isAdmin: boolean },
): { confirm: boolean } | null {
  if (status === "in_review" || !(status in MOVE_BACK_STAGE_LABEL)) return null;
  if (status === "selected") return { confirm: false };
  return isAdmin ? { confirm: true } : null;
}

/** A history entry for a move back reads "old → longlist", then the reason. */
export function movedBackEntry(note: string | null): { label: string; reason: string | null } {
  const [transition = "", ...rest] = (note ?? "").split("\n");
  const from = transition.split(" → ")[0] as StoredStatus;
  const stage = MOVE_BACK_STAGE_LABEL[from];
  const reason = rest.join("\n").trim();
  return {
    label: stage ? `Zurück in die Longlist (aus ${stage})` : "Zurück in die Longlist",
    reason: reason || null,
  };
}
